import { apiService } from '../api';
import { AxiosRequestConfig } from 'axios';
import { Clan, ClanImage } from '@/types/clan';
import type {
  SponsorPayload,
  SponsorResponse,
  ApiSuccessResponse,
} from '@/types/tournament/api';
import type { ApiResponse } from '@/types/apiResponse';
import type { ClanProfileApiResponse } from '@/types/clanProfile';
import type { SocialLinkInput } from '@/types/socialLinks';

export interface CreateClanRequest {
  name: string;
  clanSlug?: string;
  logo?: ClanImage;
  coverImage?: ClanImage;
  isActive: boolean;
  bio: string;
  verificationStatus?: string;
  isDeleted: boolean;
  members?: {
    owner: string[];
  };
}

export type ClanImageTarget = 'logo' | 'banner';

/** `setClanImage` response — the clan plus the presigned S3 URL the client uploads to. */
export type ClanImageAPIResponse = ClanCreateAPIResponse & {
  result: { presignedUrl?: string };
};

export interface GenerateInviteLinkRequest {
  contextId: string;
  role: 'owner' | 'admin';
  type: 'clan';
}

export interface InviteLinkResponse {
  query: string;
  result: string;
  error: string | null;
  totalResults: number | null;
  pageNo: number | null;
  message: string;
}

export interface ClanCreateResponse {
  logoPresignedUrl?: string;
  coverPresignedUrl?: string;
}

export interface ClanListResponse {
  query: string;
  result: Clan[];
  error: string | null;
  totalResults: number | null;
  pageNo: number | null;
  message: string;
}

export interface ClanCreateAPIResponse {
  query: string;
  result: ClanCreateResponse;
  error: string | null;
  totalResults: number | null;
  pageNo: number | null;
  message: string;
}

/** The membership routes return the new roster only — raw user ids, not populated members. */
export interface ClanMembershipAPIResponse {
  query: string;
  result: {
    _id: string;
    members: { owner: string[]; admin: string[]; creator: string };
  };
  error: string | null;
  message: string;
}

class ClanService {
  private static instance: ClanService;

  private constructor() {}

  public static getInstance(): ClanService {
    if (!ClanService.instance) {
      ClanService.instance = new ClanService();
    }
    return ClanService.instance;
  }

  async getClans(includeSponsors = false): Promise<ClanListResponse> {
    const params = new URLSearchParams();
    if (includeSponsors) {
      params.append('includeSponsors', 'true');
    }
    const query = params.toString();
    return apiService.get<ClanListResponse>(
      `/private/api/v1/clan/getClans${query ? `?${query}` : ''}`,
    );
  }

  async getClansLeadership(): Promise<ClanListResponse> {
    return apiService.get<ClanListResponse>(
      '/private/api/v1/clan/clans-leadership',
    );
  }

  async getClansForTournament(
    gameId: string,
    pageNo: number,
    limit: number,
  ): Promise<ClanListResponse> {
    const params = new URLSearchParams({ gameId });
    params.append('pageNo', pageNo.toString());
    params.append('limit', limit.toString());
    return apiService.get<ClanListResponse>(
      `/private/api/v1/clan/tournament-clans?${params.toString()}`,
    );
  }

  async getClanById(clanId: string): Promise<ClanListResponse> {
    const params = new URLSearchParams({
      _id: clanId,
      includeSponsors: 'true',
    });
    return apiService.get<ClanListResponse>(
      `/private/api/v1/clan/getClans?${params.toString()}`,
    );
  }

  async createClan(
    clanData: CreateClanRequest,
  ): Promise<ClanCreateAPIResponse> {
    return apiService.post<ClanCreateAPIResponse, CreateClanRequest>(
      '/private/api/v2/clan/create',
      clanData,
    );
  }

  async checkClanSlugAvailability(
    slug: string,
    excludeClanId?: string | null,
  ): Promise<ApiResponse<{ available: boolean; normalized: string }>> {
    const encoded = encodeURIComponent(slug);
    const query =
      excludeClanId && excludeClanId.length > 0
        ? `?clanId=${encodeURIComponent(excludeClanId)}`
        : '';
    return apiService.get<
      ApiResponse<{ available: boolean; normalized: string }>
    >(`/private/api/v2/clan/slug-available/${encoded}${query}`);
  }

  async deleteClan(clanId: string): Promise<ClanListResponse> {
    return apiService.delete<ClanListResponse>(
      `/private/api/v1/clan/deleteClan/${clanId}`,
    );
  }

  async generateInviteLink(
    clanId: string,
    data: GenerateInviteLinkRequest,
    config?: AxiosRequestConfig,
  ): Promise<InviteLinkResponse> {
    const url = `/private/api/v1/clan/${clanId}/generateInviteLink`;
    if (config !== undefined) {
      return apiService.post<InviteLinkResponse, GenerateInviteLinkRequest>(
        url,
        data,
        config,
      );
    }
    return apiService.post<InviteLinkResponse, GenerateInviteLinkRequest>(
      url,
      data,
    );
  }

  async createClanSponsor(
    clanId: string,
    sponsor: Omit<SponsorPayload, 'scope' | 'tournamentId'>,
  ): Promise<ApiResponse<SponsorResponse>> {
    return apiService.requestWithErrorHandling<ApiResponse<SponsorResponse>>({
      method: 'POST',
      url: `/private/api/v1/clan/${encodeURIComponent(clanId)}/sponsors`,
      data: sponsor,
    });
  }

  async updateClanSponsor(
    clanId: string,
    sponsorId: string,
    sponsor: Omit<SponsorPayload, 'scope' | 'tournamentId'>,
  ): Promise<ApiResponse<SponsorResponse>> {
    return apiService.requestWithErrorHandling<ApiResponse<SponsorResponse>>({
      method: 'PUT',
      url: `/private/api/v1/clan/${encodeURIComponent(clanId)}/sponsors/${encodeURIComponent(sponsorId)}`,
      data: sponsor,
    });
  }

  async deleteClanSponsor(
    clanId: string,
    sponsorId: string,
  ): Promise<ApiSuccessResponse> {
    return apiService.delete(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/sponsors/${encodeURIComponent(sponsorId)}`,
    );
  }

  /**
   * Clan profile (v2) — replaces sending name/bio/clanSlug through `updateClan`.
   * The body is strict server-side, so no other clan field can ride along.
   */
  async updateClanProfile(
    clanId: string,
    data: { name?: string; bio?: string; clanSlug?: string },
  ): Promise<ClanCreateAPIResponse> {
    return apiService.put<ClanCreateAPIResponse, typeof data>(
      `/private/api/v2/clan/${encodeURIComponent(clanId)}`,
      data,
    );
  }

  async setClanActivation(
    clanId: string,
    isActive: boolean,
  ): Promise<ClanCreateAPIResponse> {
    return apiService.post<ClanCreateAPIResponse, { isActive: boolean }>(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/activation`,
      { isActive },
    );
  }

  /** No body — the server owns the status, so a clan can never mark itself verified. */
  async requestClanVerification(
    clanId: string,
  ): Promise<ClanCreateAPIResponse> {
    return apiService.post<ClanCreateAPIResponse, Record<string, never>>(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/verification`,
      {},
    );
  }

  /** One route for both slots; the response carries `presignedUrl` for the S3 upload. */
  async setClanImage(
    clanId: string,
    file: { target: ClanImageTarget; name: string; size: number; type: string },
  ): Promise<ClanImageAPIResponse> {
    return apiService.put<ClanImageAPIResponse, typeof file>(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/image`,
      file,
    );
  }

  async clearClanImage(
    clanId: string,
    target: ClanImageTarget,
  ): Promise<ClanCreateAPIResponse> {
    return apiService.delete(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/image?target=${target}`,
    );
  }

  async setClanSocialLinks(
    clanId: string,
    socialLinks: SocialLinkInput[],
  ): Promise<ClanCreateAPIResponse> {
    return apiService.put<
      ClanCreateAPIResponse,
      { socialLinks: SocialLinkInput[] }
    >(`/private/api/v1/clan/${encodeURIComponent(clanId)}/social-links`, {
      socialLinks,
    });
  }

  /** Remove a clan member. The server derives roles and permissions from the id alone. */
  async removeClanMember(
    clanId: string,
    memberId: string,
  ): Promise<ClanMembershipAPIResponse> {
    return apiService.delete<ClanMembershipAPIResponse>(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/members/${encodeURIComponent(memberId)}`,
    );
  }

  /** Promote an admin to owner and leave, in one call. */
  async transferClanOwnership(
    clanId: string,
    newOwnerId: string,
  ): Promise<ClanMembershipAPIResponse> {
    return apiService.post<ClanMembershipAPIResponse, { newOwnerId: string }>(
      `/private/api/v1/clan/${encodeURIComponent(clanId)}/ownership/transfer`,
      { newOwnerId },
    );
  }

  async leaveClan(clanId: string): Promise<ClanListResponse> {
    return apiService.post<ClanListResponse, { clanId: string }>(
      '/private/api/v1/clan/leaveClan',
      { clanId },
    );
  }

  async getClanProfile(
    clanIdentifier: string,
  ): Promise<ClanProfileApiResponse> {
    const encodedIdentifier = encodeURIComponent(clanIdentifier);
    return apiService.get<ClanProfileApiResponse>(
      `/private/api/v2/clan/${encodedIdentifier}/clan-profile`,
    );
  }
}

export const clanService = ClanService.getInstance();
